import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaHandshake, FaArrowLeft } from 'react-icons/fa';
import api from '../services/api';
import toast, { Toaster } from "react-hot-toast";

const BrokerRegister = () => {
    const navigate = useNavigate();
    const formRef = useRef(null);

    const [formData, setFormData] = useState({
        fullName: '',
        agencyName: '',
        mobile: '',
        whatsapp: '',
        email: '',
        location: '',
        businessType: [],
        otherBusinessType: '',
        experience: '',
        clientBase: '',
        categories: [],
        otherCategory: '',
        verifiedProfiles: '',
        monthlySubmissions: '',
        commissionModel: '',
        reason: '',
        comments: '',
        contactMethod: []
    });

    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            let updatedList = [...(formData[name] || [])];
            if (checked) {
                updatedList.push(value);
            } else {
                updatedList = updatedList.filter(item => item !== value);
            }
            setFormData({ ...formData, [name]: updatedList });
        } else if (type === 'radio') {
            setFormData({ ...formData, [name]: value });
        } else {
            setFormData({ ...formData, [name]: value });
        }

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors({ ...errors, [name]: '' });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.fullName.trim()) newErrors.fullName = 'Full Name is required';
        if (!formData.agencyName.trim()) newErrors.agencyName = 'Agency Name is required';
        if (!formData.mobile.trim()) newErrors.mobile = 'Mobile Number is required';
        if (!formData.email.trim()) newErrors.email = 'Email Address is required';
        if (!formData.location.trim()) newErrors.location = 'Location is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const scrollToFirstError = () => {
        if (formRef.current) {
            const firstErrorField = formRef.current.querySelector('.error-text');
            if (firstErrorField) {
                firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            scrollToFirstError();
            return;
        }

        try {
            const payload = {
                name: formData.fullName,
                email: formData.email,
                mobile: formData.mobile,
                ...formData
            };

            const response = await api.post('/register-broker', payload);
            const data = response.data;

            toast.success('Broker Registration Submitted Successfully!');
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user));
            navigate('/');
        } catch (error) {
            console.error('Registration error:', error);
            const message = error.response?.data?.message || 'Registration failed';
            toast.error(message);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 font-sans">
            <Toaster position="top-center" />
            <nav className="bg-white shadow-sm py-4 sticky top-0 z-50">
                <div className="container mx-auto px-6 flex justify-between items-center">
                    <Link to="/" className="flex items-center gap-2">
                        <FaHandshake className="text-3xl text-rose-600" />
                        <span className="text-2xl font-black tracking-tight text-gray-900">
                            <span className="text-rose-600">BrokerNetwork</span>
                        </span>
                    </Link>
                    <Link to="/" className="flex items-center text-gray-600 hover:text-rose-600 font-medium">
                        <FaArrowLeft className="mr-2" /> Back to Home
                    </Link>
                </div>
            </nav>

            <div className="container mx-auto px-4 py-10">
                <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                    <div className="bg-gray-900 p-8 text-white text-center">
                        <h1 className="text-3xl font-bold mb-2">Join Our Broker Network</h1>
                        <p className="text-gray-400">Partner with us and grow your matchmaking business</p>
                    </div>

                    <form ref={formRef} onSubmit={handleSubmit} className="p-8 md:p-12 space-y-10">

                        {/* Broker Details - 3 columns */}
                        <section>
                            <h3 className="text-xl font-bold text-gray-800 border-b pb-2 mb-6">Broker Details</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Full Name</label>
                                    <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none" />
                                    {errors.fullName && <p className="error-text text-red-600 text-sm mt-1">{errors.fullName}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Broker / Agency Name</label>
                                    <input type="text" name="agencyName" value={formData.agencyName} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none" />
                                    {errors.agencyName && <p className="error-text text-red-600 text-sm mt-1">{errors.agencyName}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Mobile Number</label>
                                    <input type="tel" name="mobile" value={formData.mobile} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none" />
                                    {errors.mobile && <p className="error-text text-red-600 text-sm mt-1">{errors.mobile}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">WhatsApp Number</label>
                                    <input type="tel" name="whatsapp" value={formData.whatsapp} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Email Address</label>
                                    <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none" />
                                    {errors.email && <p className="error-text text-red-600 text-sm mt-1">{errors.email}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">City / Operating Location</label>
                                    <input type="text" name="location" value={formData.location} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none" />
                                    {errors.location && <p className="error-text text-red-600 text-sm mt-1">{errors.location}</p>}
                                </div>
                            </div>
                        </section>

                        {/* Business Profile */}
                        <section>
                            <h3 className="text-xl font-bold text-gray-800 border-b pb-2 mb-6">Business Profile</h3>

                            <div className="mb-8">
                                <label className="block text-sm font-bold text-gray-700 mb-3">Type of Business</label>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {['Matrimony', 'Marriage Bureau', 'Wedding Service Provider', 'Independent Agent'].map(type => (
                                        <label key={type} className="flex items-center space-x-3 cursor-pointer">
                                            <input type="checkbox" name="businessType" value={type} checked={formData.businessType.includes(type)} onChange={handleChange} className="w-5 h-5 text-rose-600 rounded focus:ring-rose-500" />
                                            <span className="text-gray-700">{type}</span>
                                        </label>
                                    ))}
                                    <div className="flex items-center space-x-3 col-span-1 md:col-span-2">
                                        <input type="checkbox" name="businessType" value="Other" checked={formData.businessType.includes('Other')} onChange={handleChange} className="w-5 h-5 text-rose-600 rounded focus:ring-rose-500" />
                                        <span className="text-gray-700">Other:</span>
                                        <input type="text" name="otherBusinessType" value={formData.otherBusinessType} onChange={handleChange} className="flex-1 border-b border-gray-300 focus:border-rose-500 outline-none px-2 py-1" />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Years of Experience</label>
                                    <input type="number" name="experience" value={formData.experience} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Approx. Active Client Base</label>
                                    <input type="number" name="clientBase" value={formData.clientBase} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none" />
                                </div>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-bold text-gray-700 mb-3">Categories You Handle</label>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {['Hindu', 'Muslim', 'Christian', 'Jain', 'Sikh', 'Intercaste', 'NRI'].map(cat => (
                                        <label key={cat} className="flex items-center space-x-3 cursor-pointer">
                                            <input type="checkbox" name="categories" value={cat} checked={formData.categories.includes(cat)} onChange={handleChange} className="w-5 h-5 text-rose-600 rounded focus:ring-rose-500" />
                                            <span className="text-gray-700">{cat}</span>
                                        </label>
                                    ))}
                                </div>
                                <div className="flex items-center space-x-3 mt-4">
                                    <input type="checkbox" name="categories" value="Other" checked={formData.categories.includes('Other')} onChange={handleChange} className="w-5 h-5 text-rose-600 rounded focus:ring-rose-500" />
                                    <span className="text-gray-700">Other:</span>
                                    <input type="text" name="otherCategory" value={formData.otherCategory} onChange={handleChange} className="flex-1 border-b border-gray-300 focus:border-rose-500 outline-none px-2 py-1" />
                                </div>
                            </div>
                        </section>

                        {/* Business Requirements */}
                        <section>
                            <h3 className="text-xl font-bold text-gray-800 border-b pb-2 mb-6">Business Requirements</h3>

                            <div className="mb-6">
                                <label className="block text-sm font-bold text-gray-700 mb-3">Do you provide verified profiles?</label>
                                <div className="flex space-x-8">
                                    <label className="flex items-center space-x-3 cursor-pointer">
                                        <input type="radio" name="verifiedProfiles" value="Yes" checked={formData.verifiedProfiles === 'Yes'} onChange={handleChange} className="w-5 h-5 text-rose-600 focus:ring-rose-500" />
                                        <span className="text-gray-700">Yes</span>
                                    </label>
                                    <label className="flex items-center space-x-3 cursor-pointer">
                                        <input type="radio" name="verifiedProfiles" value="No" checked={formData.verifiedProfiles === 'No'} onChange={handleChange} className="w-5 h-5 text-rose-600 focus:ring-rose-500" />
                                        <span className="text-gray-700">No</span>
                                    </label>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Expected Monthly Submissions</label>
                                    <input type="text" name="monthlySubmissions" value={formData.monthlySubmissions} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Preferred Commission Model</label>
                                    <input type="text" name="commissionModel" value={formData.commissionModel} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none" />
                                </div>
                            </div>
                        </section>

                        {/* Additional Information */}
                        <section>
                            <h3 className="text-xl font-bold text-gray-800 border-b pb-2 mb-6">Additional Information</h3>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Why do you want to partner with Kalyana Matrimony?</label>
                                    <textarea name="reason" rows="3" value={formData.reason} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none"></textarea>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Any Comments or Requirements</label>
                                    <textarea name="comments" rows="3" value={formData.comments} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none"></textarea>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-3">Preferred Contact Method</label>
                                    <div className="flex flex-wrap gap-8">
                                        {['Phone', 'WhatsApp', 'Email'].map(method => (
                                            <label key={method} className="flex items-center space-x-3 cursor-pointer">
                                                <input type="checkbox" name="contactMethod" value={method} checked={formData.contactMethod.includes(method)} onChange={handleChange} className="w-5 h-5 text-rose-600 rounded focus:ring-rose-500" />
                                                <span className="text-gray-700">{method}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </section>

                        <div className="flex justify-center">
  <button
    type="submit"
    className="w-64 py-4 bg-rose-600 text-white font-bold text-lg rounded-xl shadow-lg shadow-rose-500/30 hover:bg-rose-700 transition-all"
  >
    Submit Application
  </button>
</div>


                    </form>
                </div>
            </div>
        </div>
    );
};

export default BrokerRegister;